/**
 * Clerk session Bearer header for the paid search calls (2026-08-22).
 *
 * The require-sign-in-for-search backend refuses a fresh (paid) search unless
 * the request carries a VERIFIED Clerk session token. The client stream/tap
 * calls live in plain module functions (openSearchStream, openBroadStream,
 * ...), not React components, so we read the token from the global
 * `window.Clerk` session instead of the useAuth() hook.
 *
 * 2026-09-04 P1 fix: a search fired IMMEDIATELY on navigation (the stale-page
 * rebuild handoff) raced Clerk's script load - `window.Clerk.session` was not
 * ready yet, this returned {}, and a SIGNED-IN owner's search was refused 403
 * (prod log 21:34:42Z: signin_gate blocked, has_bearer=False, while the
 * sibling /background call a moment later carried the token fine). So we now
 * WAIT for Clerk to finish loading (bounded, 3s) before reading the session.
 * Genuinely signed-out visitors are unaffected: once Clerk reports loaded
 * with no session we return {} immediately, and the backend's refusal +
 * sign-in popup flow is unchanged.
 */
type ClerkGlobal = {
  loaded?: boolean;
  session?: { getToken: () => Promise<string | null> } | null;
};

function clerkGlobal(): ClerkGlobal | undefined {
  return (window as unknown as { Clerk?: ClerkGlobal }).Clerk;
}

async function waitForClerk(timeoutMs = 3000): Promise<ClerkGlobal | undefined> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const clerk = clerkGlobal();
    // Ready = the script finished initialising (loaded flag), or the session
    // object is already present (covers older Clerk globals without .loaded).
    if (clerk && (clerk.loaded === true || clerk.session !== undefined)) {
      return clerk;
    }
    if (Date.now() >= deadline) return clerk;
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
}

export async function bearerHeaders(): Promise<Record<string, string>> {
  if (typeof window === "undefined") return {};
  try {
    const clerk = await waitForClerk();
    const token = clerk?.session ? await clerk.session.getToken() : null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}
